/**
 * Cart Store - Shopify Cart API Integration
 * Handles all cart operations using Shopify's Cart API
 */

const CartStore = {
  // Cache for cart data
  _cartData: null,
  _lastFetch: null,
  _cacheTimeout: 5000, // 5 seconds cache
  _lastCartCount: null,

  /**
   * Fetch cart data from Shopify
   * @returns {Promise<Object>} Cart data from Shopify
   */
  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`Cart fetch failed: ${response.status}`);
      }
      const data = await response.json();
      this._cartData = data;
      this._lastFetch = Date.now();
      return data;
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw error;
    }
  },

  /**
   * Get cart data (with caching)
   * @returns {Promise<Object>} Cart data
   */
  async getCart() {
    const now = Date.now();
    if (this._cartData && this._lastFetch && (now - this._lastFetch) < this._cacheTimeout) {
      return this._cartData;
    }
    return await this.fetchCart();
  },

  /**
   * Get cart items
   * @returns {Promise<Array>} Array of cart items
   */
  async getItems() {
    try {
      const cart = await this.getCart();
      return cart.items || [];
    } catch (error) {
      console.error('Error getting cart items:', error);
      return [];
    }
  },

  /**
   * Add item to cart
   * @param {number} variantId - Variant ID
   * @param {number} quantity - Quantity to add
   * @param {Object} properties - Optional properties
   * @returns {Promise<Object>} Updated cart data
   */
  async addItem(variantId, quantity = 1, properties = {}) {
    try {
      // Shopify Cart API accepts both single item format and items array format
      // Using items array format for consistency with product-form.liquid
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: quantity,
            properties: properties,
          }],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || `Failed to add item: ${response.status}`);
      }

      // Invalidate cache and fetch fresh cart data
      this._cartData = null;
      const cart = await this.fetchCart();
      
      // Update cart count badge
      this.updateCartCount(cart.item_count);
      
      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      
      return cart;
    } catch (error) {
      console.error('Error adding item to cart:', error);
      throw error;
    }
  },

  /**
   * Update quantity for a specific line item
   * @param {number} lineItemKey - Line item key (from cart.items[].key)
   * @param {number} quantity - New quantity
   * @returns {Promise<Object>} Updated cart data
   */
  async updateQuantity(lineItemKey, quantity) {
    try {
      if (quantity <= 0) {
        return await this.removeItem(lineItemKey);
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineItemKey,
          quantity: quantity,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || `Failed to update quantity: ${response.status}`);
      }

      // Invalidate cache and fetch fresh cart data
      this._cartData = null;
      const cart = await this.fetchCart();
      
      // Update cart count badge
      this.updateCartCount(cart.item_count);
      
      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      
      return cart;
    } catch (error) {
      console.error('Error updating quantity:', error);
      throw error;
    }
  },

  /**
   * Remove item from cart
   * @param {number} lineItemKey - Line item key (from cart.items[].key)
   * @returns {Promise<Object>} Updated cart data
   */
  async removeItem(lineItemKey) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineItemKey,
          quantity: 0,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || `Failed to remove item: ${response.status}`);
      }

      // Invalidate cache and fetch fresh cart data
      this._cartData = null;
      const cart = await this.fetchCart();
      
      // Update cart count badge
      this.updateCartCount(cart.item_count);
      
      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      
      return cart;
    } catch (error) {
      console.error('Error removing item from cart:', error);
      throw error;
    }
  },

  /**
   * Get cart totals
   * @returns {Promise<Object>} Object with subtotal, totalItems, etc.
   */
  async getTotals() {
    try {
      const cart = await this.getCart();
      return {
        subtotal: cart.total_price / 100, // Convert from cents to currency unit
        totalItems: cart.item_count,
        totalPrice: cart.total_price / 100,
        originalTotalPrice: cart.original_total_price / 100,
      };
    } catch (error) {
      console.error('Error getting cart totals:', error);
      return {
        subtotal: 0,
        totalItems: 0,
        totalPrice: 0,
        originalTotalPrice: 0,
      };
    }
  },

  /**
   * Update cart count badge in header
   * @param {number} count - Cart item count
   */
  updateCartCount(count) {
    const cartCountElement = document.getElementById('cart-count');
    if (cartCountElement) {
      if (count > 0) {
        cartCountElement.textContent = count;
        cartCountElement.style.display = '';
      } else {
        cartCountElement.textContent = '';
        cartCountElement.style.display = 'none';
      }
    }
    
    const shouldPulse = this._lastCartCount !== null && count > 0 && count > this._lastCartCount;
    this._lastCartCount = count;
    
    if (shouldPulse) {
      const iconWrap = document.querySelector('.site-header__cart-icon-wrap');
      if (iconWrap) {
        iconWrap.classList.remove('is-pulsing');
        void iconWrap.offsetWidth;
        iconWrap.classList.add('is-pulsing');
        setTimeout(() => {
          iconWrap.classList.remove('is-pulsing');
        }, 260);
      }
    }
  },

  /**
   * Clear cart cache (useful for manual refresh)
   */
  clearCache() {
    this._cartData = null;
    this._lastFetch = null;
  },

  /**
   * Initialize cart store - fetch initial cart data and update badge
   */
  async init() {
    try {
      const cart = await this.fetchCart();
      this.updateCartCount(cart.item_count);
    } catch (error) {
      console.error('Error initializing cart store:', error);
      this.updateCartCount(0);
    }
  },
};

// Initialize cart store on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    CartStore.init();
  });
} else {
  CartStore.init();
}

// Export for use in other scripts
window.CartStore = CartStore;

// Initialize add-to-cart buttons from product cards
(function() {
  'use strict';
  
  function initAddToCartButtons() {
    const addToCartButtons = document.querySelectorAll('.js-add-to-cart');
    
    addToCartButtons.forEach(button => {
      // Remove existing listeners to prevent duplicates
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
      
      newButton.addEventListener('click', async function(e) {
        e.preventDefault();
        
        const variantId = parseInt(this.dataset.variantId);
        const productId = this.dataset.productId;
        const productTitle = this.dataset.title || 'Produkt';
        
        if (!variantId) {
          console.error('Variant ID missing');
          alert('Kunde inte lägga till produkt i kundvagnen. Försök igen.');
          return;
        }
        
        // Disable button during request
        const originalHTML = this.innerHTML;
        this.disabled = true;
        this.innerHTML = '<span>Lägger till...</span>';
        
        try {
          await CartStore.addItem(variantId, 1);
          
          // Show success feedback
          this.innerHTML = '<span>✓ Tillagd!</span>';
          
          // Open cart drawer if available
          if (window.CartDrawer) {
            setTimeout(() => {
              CartDrawer.open();
            }, 300);
          }
          
          // Reset button after delay
          setTimeout(() => {
            this.disabled = false;
            this.innerHTML = originalHTML;
          }, 2000);
          
        } catch (error) {
          console.error('Error adding to cart:', error);
          alert('Kunde inte lägga till ' + productTitle + ' i kundvagnen. Försök igen.');
          this.disabled = false;
          this.innerHTML = originalHTML;
        }
      });
    });
  }
  
  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAddToCartButtons);
  } else {
    initAddToCartButtons();
  }
  
  // Re-initialize when cart is updated (for dynamically loaded content)
  document.addEventListener('cart:updated', () => {
    setTimeout(initAddToCartButtons, 100);
  });
})();